import type { AssessmentType, CareUser } from "@/lib/api/care";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { buildRedactedPseudoId } from "@/lib/redacted-display";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@somethings-ui/web";
import { Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import type { ButtonHTMLAttributes, ReactNode } from "react";

export const ASSESSMENT_NAMES: Record<AssessmentType, string> = {
  phq9: "PHQ-9",
  gad7: "GAD-7",
  who5: "WHO-5",
};

export const ASSESSMENT_MAX_SCORES: Record<AssessmentType, number> = {
  phq9: 27,
  gad7: 21,
  who5: 100,
};

export function formatCareDate(value: string | null | undefined) {
  return value ? DateTime.fromISO(value).toFormat("LLL d, yyyy") : "";
}

export function CareCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-[20px] border border-[#dfddd8] bg-white px-6 py-4",
        className,
      )}
    >
      {children}
    </section>
  );
}

export function FieldLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p className={cn("font-body text-xs leading-4 text-[#6e6b67]", className)}>
      {children}
    </p>
  );
}

export function Field({
  label,
  children,
  className,
  labelClassName,
  valueClassName,
}: {
  label: string;
  children: ReactNode;
  className?: string;
  labelClassName?: string;
  valueClassName?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <FieldLabel className={labelClassName}>{label}</FieldLabel>
      <div
        className={cn(
          "font-body text-base leading-6 text-[#1f1e1d]",
          valueClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}

export type PillTone =
  | "neutral"
  | "escalation"
  | "flag"
  | "changed"
  | "review"
  | "ivory"
  | "ivoryLight"
  | "sky"
  | "outline"
  | "crisis";

const PILL_TONES: Record<PillTone, string> = {
  neutral: "bg-[#dcdcdc] text-[#525252]",
  escalation: "bg-[#ffd9df] text-[#f93b51]",
  flag: "bg-[#ffd49c] text-[#844b04]",
  changed: "bg-[#c3c4ff] text-[#0713f6]",
  review: "border border-[#82734f] text-[#82734f]",
  ivory: "bg-[#ebe0cc] text-[#735f31]",
  ivoryLight: "bg-[#f8f4ed] text-[#735f31]",
  sky: "bg-[#b8f1ff] text-[#005b76]",
  outline: "border border-[#aeaeae] bg-white text-[#3c3c3c]",
  crisis: "bg-[#f93b51] text-[#ffeaee]",
};

export function Pill({
  tone = "neutral",
  children,
  className,
}: {
  tone?: PillTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-center font-body text-xs leading-4",
        PILL_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function CodeChip({
  code,
  size = "lg",
}: {
  code: string;
  size?: "sm" | "lg";
}) {
  return (
    <span
      className={cn(
        "inline-flex h-[22px] items-center rounded border border-[#dfddd8] bg-[#f6f4ef] px-1 py-0.5 font-mono text-[#58534b]",
        size === "lg" ? "text-base leading-5" : "text-xs font-medium leading-4",
      )}
    >
      {code}
    </span>
  );
}

export function ScoreTag({
  score,
  severity,
  max,
  size = "md",
}: {
  score: number;
  severity: string;
  max?: number;
  size?: "sm" | "md";
}) {
  if (size === "md") {
    return (
      <span className="inline-flex min-h-8 items-center">
        <span className="pr-1 font-heading text-2xl font-bold leading-8 text-black">
          {score}
        </span>
        <span className="inline-flex min-h-6 items-center rounded bg-[rgba(160,153,141,0.16)] py-1 pl-[5px] pr-2 font-body text-xs leading-4 text-black">
          {severity}
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-heading text-xl font-bold leading-6 text-[#1f1e1d]">
        {score}
        {max ? (
          <span className="font-semibold text-[#a0998d]"> / {max}</span>
        ) : null}
      </span>
      <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 font-body text-xs leading-4 text-[#58534b]">
        {severity}
      </span>
    </span>
  );
}

export function UserChip({
  user,
  linked = false,
}: {
  user: CareUser;
  linked?: boolean;
}) {
  const hideIdentity =
    useDevOverridesStore((state) => state.hidePhiPii) === true &&
    user.role.toLowerCase() === "mentee";
  const label = hideIdentity
    ? buildRedactedPseudoId(user.id)
    : (user.name ?? "");
  const photo = useSignedMedia(
    hideIdentity ? null : user.profile_picture_url,
    user.id,
  );
  const className =
    "inline-flex items-center gap-2 rounded-full bg-[rgba(160,153,141,0.1)] py-1.5 pl-2 pr-3";
  const content = (
    <>
      <UserAvatar
        name={hideIdentity ? "?" : (user.name ?? "")}
        imageUrl={hideIdentity ? undefined : photo.url}
        size={16}
      />
      <span className="font-body text-xs leading-4 text-black">{label}</span>
    </>
  );
  if (!linked) return <span className={className}>{content}</span>;
  return (
    <Link
      to="/users/$userId"
      params={{ userId: user.id }}
      className={cn(
        className,
        "hover:bg-[rgba(160,153,141,0.2)] focus-visible:bg-[rgba(160,153,141,0.2)]",
      )}
    >
      {content}
    </Link>
  );
}

export function SectionHeader({
  title,
  action,
  muted,
}: {
  title: string;
  action?: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="flex min-h-9 items-center justify-between">
      <FieldLabel className={muted ? "text-[#b5b0a6]" : undefined}>
        {title}
      </FieldLabel>
      {action}
    </div>
  );
}

type CareButtonVariant = "tonal" | "filled" | "outline";

const BUTTON_VARIANTS: Record<CareButtonVariant, string> = {
  tonal:
    "border border-[rgba(160,153,141,0.16)] bg-[rgba(160,153,141,0.08)] text-black",
  filled: "bg-black text-white disabled:bg-[#dfddd8] disabled:text-[#a0998d]",
  outline: "border border-[#8a8275] bg-white text-black",
};

export function CareButton({
  variant = "tonal",
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: CareButtonVariant;
}) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-8 min-w-[120px] items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 font-body text-xs leading-4 disabled:opacity-60",
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
